import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Crown, Heart, Star, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { SERIES_PLACE } from "@/lib/bibleMap";
import { ERAS } from "@/pages/Timeline";
import { BibleMap, MapBoat, Stop } from "./BibleMap";
import { Emblem } from "./Emblem";

const PALETTE = ["#E07A5F", "#3D7EA6", "#81B29A", "#D97706", "#9B5DE5", "#E9A23B", "#2A9D8F", "#C86D51"];
const ORDER = ERAS.flatMap((e) => e.items.map(([id]) => id));

function CheerInbox({ kidId }: { kidId: string }) {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    api.get(`/kids/${kidId}/cheers/new`).then((r) => {
      if (!r.data.length) return;
      api.post(`/kids/${kidId}/cheers/ack`, { ids: r.data.map((c: any) => c.id) }).catch(() => {});
      const by: Record<string, { star: number; heart: number }> = {};
      r.data.forEach((c: any) => { by[c.from_name] ||= { star: 0, heart: 0 }; by[c.from_name][c.kind as "star" | "heart"] += 1; });
      const parts = Object.entries(by).map(([n, c]) => `${n} sent you ${[c.star && `${c.star} star${c.star > 1 ? "s" : ""}`, c.heart && `${c.heart} heart${c.heart > 1 ? "s" : ""}`].filter(Boolean).join(" and ")}`);
      setMsg(parts.join(" · ") + "!");
      confetti({ particleCount: 90, spread: 80, origin: { y: 0.3 }, colors: ["#FBBF24", "#F87171", "#FDE68A"], disableForReducedMotion: true });
    }).catch(() => {});
  }, [kidId]);
  return (
    <AnimatePresence>
      {msg && (
        <motion.div initial={{ opacity: 0, y: -10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }} data-testid="cheer-inbox"
          className="mb-5 flex items-center gap-3 rounded-2xl bg-gradient-to-r from-amber-100 to-rose-100 px-5 py-4 text-lg font-semibold text-slate-800 shadow">
          <span className="flex gap-1"><Star className="h-6 w-6 fill-amber-400 text-amber-400" /><Heart className="h-6 w-6 fill-rose-400 text-rose-400" /></span>
          <span className="flex-1">{msg}</span>
          <button onClick={() => setMsg(null)} data-testid="cheer-inbox-close" className="rounded-full bg-white/70 px-3 py-1 text-sm">Yay!</button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CheerCard({ c, color, canCheer, onCheer, kids, leader }: { c: any; color: string; canCheer: boolean; onCheer: (kind: string) => void; kids?: boolean; leader?: boolean }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-[#E2DBCE]" data-testid={`family-member-${c.id}`}>
      <div className="relative rounded-full p-0.5" style={{ background: color }}>
        {leader && <Crown className="absolute -top-4 left-1/2 h-6 w-6 -translate-x-1/2 fill-amber-300 text-amber-500 drop-shadow" data-testid={`catch-crown-${c.id}`} />}
        <Emblem avatar={c.avatar} size={kids ? 48 : 40} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`font-semibold text-slate-900 ${kids ? "text-lg" : ""}`}>{c.name}</p>
        <p className="text-xs text-slate-500" data-testid={`family-caught-today-${c.id}`}>{c.caught_today || 0} caught today{leader ? " · top catcher!" : ""}</p>
        <p className="text-xs text-slate-500" data-testid={`family-cheer-count-${c.id}`}>{c.stories} stories · {c.cheers.star} <Star className="inline h-3 w-3 fill-amber-400 text-amber-400" /> {c.cheers.heart} <Heart className="inline h-3 w-3 fill-rose-400 text-rose-400" /> this week</p>
      </div>
      {canCheer ? (
        <div className="flex gap-1.5">
          <button data-testid={`cheer-star-${c.id}`} onClick={() => onCheer("star")} aria-label={`Send ${c.name} a star`} className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 transition-transform hover:scale-110 active:scale-95"><Star className="h-5 w-5 fill-amber-400 text-amber-500" /></button>
          <button data-testid={`cheer-heart-${c.id}`} onClick={() => onCheer("heart")} aria-label={`Send ${c.name} a heart`} className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 transition-transform hover:scale-110 active:scale-95"><Heart className="h-5 w-5 fill-rose-400 text-rose-500" /></button>
        </div>
      ) : <span className="rounded-full bg-[#F3ECE0] px-3 py-1 text-xs font-semibold text-slate-600">That's you!</span>}
    </div>
  );
}

export function FamilyMap({ kid }: { kid?: any }) {
  const [data, setData] = useState<any>(null);
  useEffect(() => { api.get(kid ? `/kids/${kid.id}/family` : "/family/map").then((r) => setData(r.data)).catch(() => {}); }, [kid]);
  if (!data || data.children.length < (kid ? 2 : 1)) return <>{kid && <CheerInbox kidId={kid.id} />}</>;
  const byId = Object.fromEntries(data.series.map((s: any) => [s.id, s]));
  const ids = ORDER.filter((id) => byId[id]);
  const stops: Stop[] = ids.map((id) => ({ key: id, place: SERIES_PLACE[id], label: byId[id].title, href: kid ? "/kids" : `/boat/${id}`, done: false, image: byId[id].cover }));
  const boats: MapBoat[] = data.children.map((c: any, i: number) => ({ key: c.id, name: c.name, color: PALETTE[i % PALETTE.length], idx: Math.max(0, ...ids.map((id, k) => (c.series_done[id] ? k : 0))) }));
  const top = Math.max(0, ...data.children.map((c: any) => c.caught_today || 0));
  const cheer = async (to: string, kind: string) => {
    try {
      await api.post(kid ? `/kids/${kid.id}/cheer` : "/family/cheer", { to_child_id: to, kind });
      setData({ ...data, children: data.children.map((c: any) => (c.id === to ? { ...c, cheers: { ...c.cheers, [kind]: c.cheers[kind] + 1 } } : c)) });
      confetti({ particleCount: 40, spread: 60, origin: { y: 0.7 }, colors: kind === "star" ? ["#FBBF24", "#FDE68A"] : ["#F87171", "#FDA4AF"], disableForReducedMotion: true });
      toast.success(kind === "star" ? "Star sent!" : "Heart sent!");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
    {kid && <CheerInbox kidId={kid.id} />}
    <section className={`rounded-[2rem] ${kid ? "bg-white/80 p-5 shadow-lg sm:p-7" : "card-warm p-6 sm:p-8"}`} data-testid="family-map">
      <div className="mb-4 flex items-center gap-2"><Users className="h-5 w-5 text-[#C86D51]" /><h2 className={`font-semibold text-slate-900 ${kid ? "text-3xl" : "font-display text-2xl"}`}>Family journey</h2></div>
      <p className="mb-5 text-sm text-slate-500">{kid ? "See where everyone's boat is on the Bible journey, and send a cheer!" : "Each child's boat sits at the furthest story series they've reached. Send a star or heart to cheer them on."}</p>
      <BibleMap stops={stops} boats={boats} testId="family-bible-map" />
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {data.children.map((c: any, i: number) => <CheerCard key={c.id} c={c} color={boats[i].color} kids={!!kid} leader={data.children.length > 1 && c.caught_today > 0 && c.caught_today === top} canCheer={!kid || c.id !== kid.id} onCheer={(k) => cheer(c.id, k)} />)}
      </div>
    </section>
    </>
  );
}
